import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, SafeAreaView, Text, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import { useAccount } from '../../../hooks/useAccount';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import { useFocusEffect, useTheme } from '@react-navigation/native';
import axios from 'axios';
import { Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { openFile } from '../../../components/Platform/fileViewer';
import RNFS from '../../../components/Platform/rnfs';
import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons/static";
import CustomButton from '../../../components/CustomButton';
// * Language 
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from "react-native-safe-area-context";

const MyAccountDocumentsScreen = ({ navigation, route }) => {

    const { colors } = useTheme();
    const styles = makeStyles(colors);
    const { t } = useTranslation();
    const [documents, setDocuments] = useState([]);
    const { getDocumentsAsync, isLoading } = useAccount();
    const insets = useSafeAreaInsets();

    //* Helpers for open/download documents on mobile
    // Replace characters that are not allowed in file names (important for Android)
    const sanitizeFileName = (name) => (name || "document").replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");

    // Save base64 data to a real file on the device and return the local path
    const saveBase64ToFile = async (item) => {
        const ext = (item.type || "bin").toLowerCase(); // pdf, png, jpg, etc.
        const fileName = `${sanitizeFileName(item.name)}.${ext}`;

        // App-private documents folder (works on iOS + Android without extra permissions)
        const path = `${RNFS.DocumentDirectoryPath}/${fileName}`;

        // We only want the "AAAA..." part for RNFS.writeFile
        const base64 = (item.data || ";").includes("base64,")
            ? (item.data || ";").split("base64,")[1] : item.data;

        // Write the file to disk
        await RNFS.writeFile(path, base64, "base64");

        return path;
    };

    //* This is called from the useEffect and also from on refresh 
    const pickDocument = useCallback(async (item) => {
        if (Platform.OS === 'web') {
            // create blob
            const blobData = await fetch(`data:plain/text;base64,${item.data}`).then(res => {
                return res.blob();
            });
            // create download link
            const url = URL.createObjectURL(blobData);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `${item.name}.${item.type}`,
            );

            // insert link on page
            document.body.appendChild(link);

            // start download
            link.click();

            // remove link
            link.parentNode.removeChild(link);
        }
    }, []);

    useEffect(() => {
        const onGetDocuments = async () => {
            await getDocumentsAsync().then((response) => {
                setDocuments(response)
            }).catch((error) => {
                console.log(error)
            });
        };
        onGetDocuments()
            .catch(console.error);
    },[]);

    // TODO
    function axiosErrorHandling(error) {
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx

            console.log(error.response.data);
            console.log(error.response.status);
            console.log(error.response.headers);
        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js

            // console.log(error.request.response);
            showMessage({
                message: error.request.response,
                type: "warning",
                duration: 10000,
            });
        } else {
            // Something happened in setting up the request that triggered an Error

            console.log('Error: ', error.message);
        };
    };

    // TODO: currently works for pdf only
    // const openDocument = (item) => {
    //     // create a path you want to write to
    //     var path = FileSystem.documentDirectory + '/' + item.name + '.' + item.type;
    //     // write the file
    //     FileSystem.writeFile(path, item.data, 'base64')
    //         .then((success) => {
    //             console.log('FILE WRITTEN at ' + path);
    //         })
    //         .catch((err) => {
    //             console.log(err.message);
    //         });

    //     FileViewer.open(path)
    //         .then(() => {
    //             // console.log("success");
    //         })
    //         .catch((error) => {
    //             console.log(error);
    //             // axiosErrorHandling(error);
    //         });
    // };

    const openDocument = async (file) => {
        if (Platform.OS === "web") {
            const newWindow = window.open('', '_blank');
            let uri = file.data;
            let type = file.type;
            let style = 'width="100%" height="100%"';

            if (file.type === 'pdf') {
                type = 'application/pdf'
            }
            else if (file.type === 'png' || file.type === 'jpg') {
                type = `image/${file.type}`
                style = 'width="500" height="500"'
            }

            if (!uri.includes("base64")) {
                uri = `data:${type};base64,${uri}`
            }

            newWindow.document.write(`<html> <body> <embed src="${uri}" ${style}/> </body> </html>`);
            return;
        } else {
            //* For mobile save file locally then open it with native viewer
            const path = await saveBase64ToFile(file);
            
            // showOpenWithDialog: lets user choose an app if multiple exist
            await openFile(path, file.type);
        }
    }

    const downloadDocument = async (item) => {
        if (Platform.OS === "web") {
            await pickDocument(item);
            return;
        } else {
            //* For mobile save it locally
            const path = await saveBase64ToFile(item);

            // Open it (this is the closest equivalent to “download then open”)
            await openFile(path, item.type);
        }
    };

    //* Display file image based on type
    const renderDocumentImage = (type) => {
        if (type.toLowerCase().includes('pdf')) {
            return (
                <MaterialCommunityIcons
                    name="file-pdf-box"
                    style={styles.documentIcon}
                />
            )
        } else if (type.toLowerCase().includes('png')) {
            return (
                <MaterialCommunityIcons
                    name="file-png-box"
                    style={styles.documentIcon}
                />
            )
        } else if (type.toLowerCase().includes('gif')) {
            return (
                <MaterialCommunityIcons
                    name="file-gif-box"
                    style={styles.documentIcon}
                />
            )
        } else if (type.toLowerCase().includes('jp')) {
            return (
                <MaterialCommunityIcons
                    name="file-jpg-box"
                    style={styles.documentIcon}
                />
            )
        } else if (type.toLowerCase().includes('doc')) {
            return (
                <MaterialCommunityIcons
                    name="file-word-box"
                    style={styles.documentIcon}
                />
            )
        } else if (type.toLowerCase().includes('xls')) {
            return (
                <MaterialCommunityIcons
                    name="file-excel-box"
                    style={styles.documentIcon}
                />
            )
        };
    };

    //* Render flat list data
    const renderData = (item) => {
        return (
            <Card
                style={styles.cardstyle}
                onPress={() => openDocument(item)} >
                <Card.Content>
                    <SafeAreaView style={styles.documentContainer}>
                        {renderDocumentImage(item.type)}
                        <CustomLabel
                            type="ACCOUNT"
                            value={item.name.toLowerCase()} />
                    </SafeAreaView>
                </Card.Content>
                <Card.Actions>
                    <CustomButton
                        iconName='cloud-download-outline'
                        onPress={async () => {
                            await downloadDocument(item);
                        }}
                        show='HOME_BELL'
                        type='HOME_BELL'
                        textTop='2'
                        sz={20} />
                </Card.Actions>
            </Card >
        )
    };

    return (
        <SafeAreaView style={styles.mainView}>
            <FlatList
                ListEmptyComponent={
                    documents?.length > 0 ?
                        <ActivityIndicator animating={true} color={colors.blue} />
                        :
                        <CustomLabel
                            type="ACCOUNT"
                            value={t('common:noDocuments')} />
                }
                data={documents}
                renderItem={({ item }) => {
                    return (renderData(item))
                }}
                onRefresh={() => getDocumentsAsync()}
                refreshing={isLoading}
                keyExtractor={item => item.id}
                contentContainerStyle={{
                    paddingBottom: Math.max(insets.bottom + 24, 40),
                }}
            />
        </SafeAreaView>
    )
};


const makeStyles = (colors) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    icons: {
        fontSize: 22,
        color: colors.text_primary,
    },
    documentContainer: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%'
    },
    documentIcon: {
        fontSize: 45,
        marginRight: '3%',
        color: colors.text_primary
    },
    cardstyle: {
        padding: 10,
        margin: 20,
        flex: 1,
        borderRadius: 35,
        backgroundColor: colors.background_card,
        elevation: 10,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 10,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        flex: 1,
        width: '95%',
        marginTop: 1
    },
});

export default MyAccountDocumentsScreen;